import _ from 'lodash';

export function computeLogReturns(prices) {
    return prices.slice(1).map((price, index) => Math.log(price / prices[index]));
}


export const percentReturn = (sell, buy)=>{
    if (sell===0||buy===0){
        return 0
    }
    return (sell - buy) / buy;
}

export const costOfLoss = (pct)=>{return (1 / (1 - Math.abs(pct))) - 1;}

export function rollingStdev(values, windowSize) {
    let result = [];

    for (let i = 0; i <= values.length - windowSize; i++) {
        let window = values.slice(i, i + windowSize);
        let mean = window.reduce((sum, val) => sum + val, 0) / window.length;
        let stdev = Math.sqrt(window.reduce((sum, val) => sum + Math.pow((val / mean) - 1, 2), 0) / (window.length - 1));  // Change here for sample standard deviation
        result.push(stdev);
    }
    return  result;
}

export function rollingSum(values, windowSize) {
    let result = [];
    for (let i = 0; i <= values.length - windowSize; i++) {
        let window = values.slice(i, i + windowSize);
        let sum = window.reduce((sum, val) => sum + val, 0);
        result.push(sum);
    }
    return result;
}

export function logToPercentReturns(logReturns) {
    let cumulative = 0;
    return logReturns.map(logReturn => {
        cumulative += logReturn;
        return (Math.exp(cumulative) - 1);
    });
}

export function rollingAverage(values, windowSize) {
    let result = [];
    for (let i = 0; i <= values.length - windowSize; i++) {
        let window = values.slice(i, i + windowSize);
        let average = window.reduce((sum, val) => sum + val, 0) / windowSize;
        result.push(average);
    }
    return result;
}


/**
 * Generates a target path based on initial value, years, time steps per year, and total return.
 *
 * @param {number} initialValue - The starting value.
 * @param {number} years - The number of years.
 * @param {number} timeStepsPerYear - The number of time steps per year.
 * @param {number} totalReturn - The total return over the period.
 * @returns {number[]} An array representing the target path.
 */
/**
 * Generates a target path based on initial value, years, time steps per year, and total return,
 * incorporating a Bézier-like curve for a smoother, more realistic path.
 *
 * @param {number} initialValue - The starting value.
 * @param {number} years - The number of years.
 * @param {number} timeStepsPerYear - The number of time steps per year.
 * @param {number} totalReturn - The total return over the period.
 * @param {number} [controlPointBias=0.5] - Bias for the control point to adjust the curvature (between 0 and 1).
 * @returns {number[]} An array representing the target path with Bézier-like smoothing.
 */
export function realizeTargetPath(initialValue, years, timeStepsPerYear, totalReturn, controlPointBias = 0.6) {
    const totalSteps = Math.floor(years * timeStepsPerYear);
    const annualReturn = Math.pow(1 + totalReturn, 1 / years) - 1;
    const dailyReturn = Math.pow(1 + annualReturn, 1 / timeStepsPerYear) - 1;

    // Ensure dailyReturn is negative if totalReturn is negative
    const signedDailyReturn = totalReturn < 0 ? -Math.abs(dailyReturn) : dailyReturn;

    const finalValue = initialValue * Math.pow(1 + signedDailyReturn, totalSteps);

    // Control point for Bézier curve: halfway between initial and final value
    const controlPointValue = initialValue + controlPointBias * (finalValue - initialValue);

    const path = new Array(totalSteps + 1);

    for (let i = 0; i <= totalSteps; i++) {
        const t = i / totalSteps;  // Time parameter for the Bézier curve (ranges from 0 to 1)
        // Bézier formula: B(t) = (1 - t)^2 * P0 + 2 * (1 - t) * t * P1 + t^2 * P2
        path[i] = (1 - t) * (1 - t) * initialValue + 2 * (1 - t) * t * controlPointValue + t * t * finalValue;
    }

    return path;
}


export function rollingCorrelation(x, y, window = 30) {
    if (x.length !== y.length) {
        throw new Error("Input arrays must have the same length");
    }

    let result = [];

    for (let i = 0; i <= x.length - window; i++) {
        const xWindow = x.slice(i, i + window);
        const yWindow = y.slice(i, i + window);

        const xMean = _.mean(xWindow);
        const yMean = _.mean(yWindow);

        const covariance = _.sum(xWindow.map((xi, index) => (xi - xMean) * (yWindow[index] - yMean)));
        const xVariance = _.sum(xWindow.map(xi => Math.pow(xi - xMean, 2)));
        const yVariance = _.sum(yWindow.map(yi => Math.pow(yi - yMean, 2)));

        const correlation = covariance / Math.sqrt(xVariance * yVariance);
        result.push(correlation);
    }

    return result;
}



/**
 * Calculates the cumulative sum of an array.
 *
 * @param {number[]} arr - The input array.
 * @returns {number[]} The cumulative sum array.
 */
export function cumsum(arr) {
    return arr.reduce((acc, current, index) => {
        acc.push(index === 0 ? current : acc[index - 1] + current);
        return acc;
    }, []);
}
